import Layout from '@/components/Layout'
import { useState, useEffect } from 'react'
import '../reports/style.css'
import { createMultiLogin, getMultiLoginList } from '@/services/multiLoginApi'
import { Toast } from 'bootstrap/dist/js/bootstrap.bundle.min'

/**
 * CreateAccount Component
 * 
 * Page for creating user accounts with personal information and privileges.
 */
function CreateAccount() {
  const [formData, setFormData] = useState({
    clientId: '',
    fullName: '',
    password: '',
    confirmPassword: '',
    masterPassword: ''
  })

  const [privileges, setPrivileges] = useState({
    marketAnalysis: false,
    clientList: false,
    withdraw: false,
    deposit: false,
    userStatus: true, // Checked by default
    userUpdate: false,
    changePassword: true, // Checked by default
    bankList: false,
    accountStatement: false,
    myBets: false,
    casinoReport: false,
    userLogs: false,
    gameReport: false,
    fraudReport: false,
    casinoList: false,
    gameList: false,
    creditReference: false,
    exposureLimit: false,
    userCreate: false
  })

  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)

  // Sample table data - empty initially
  useEffect(() => {
    setTableData([])
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePrivilegeChange = (privilege) => {
    setPrivileges(prev => ({
      ...prev,
      [privilege]: !prev[privilege]
    }))
  }

   useEffect(() => {
  fetchExecutives();
}, [])

  const fetchExecutives = async () => {
  try {
    const res = await getMultiLoginList()

    if (!res?.success) {
      Toast.fire({ icon: 'error', title: 'Failed to load executives' })
      return
    }

    const mapped = res.executives.map(exec => {
      const p = exec.permissions || {}

      return {
        action: exec.active ? 'Active' : 'Inactive',
        username: exec.username,
        fullName: exec.email || '-', // backend doesn’t send fullName yet

        dashboard: !!p.dashboard,
        marketAnalysis: !!p.marketAnalysis,
        userList: !!p.userList,
        insertUser: !!p.insertUser,
        accountStatement: !!p.accountStatement,
        partyWinLoss: !!p.partyWinLoss,
        currentBets: !!p.currentBets,
        generalLock: !!p.generalLock,
        casinoResult: !!p.casinoResult,
        liveCasinoResult: !!p.liveCasinoResult,
        our: !!p.ourCasino
      }
    })

    setTableData(mapped)
  } catch (err) {
    console.error(err)
    Toast.fire({
      icon: 'error',
      title: err.message || 'Error fetching executives'
    })
  }
}


  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation
    if (!formData.clientId || !formData.fullName || !formData.password || !formData.confirmPassword) {
      Toast.fire({
        icon: 'error',
        title: 'Please fill in all required fields'
      });
      return
    }

    if (formData.password !== formData.confirmPassword) {
      Toast.fire({
        icon: 'error',
        title: 'Password and Confirm Password do not match'
      });
      return
    }

    if (!formData.masterPassword) {
      Toast.fire({
        icon: 'error',
        title: 'Please enter Transaction Code'
      });
      return
    }

    setLoading(true)
    try {
      // Transform privileges object to permissions format
      const payload = {
        username: formData.clientId,
        password: formData.password,
        permissions: privileges
      }

      const response = await createMultiLogin(payload)
      
      Toast.fire({
        icon: 'success',
        title: response.message || 'Multi Login account created successfully'
      });

      // Reset form
      setFormData({
        clientId: '',
        fullName: '',
        password: '',
        confirmPassword: '',
        mpass: ''
      })
      setPrivileges({
        all: false,
        currentBets: false,
        loginUserCreation: false,
        betLock: false,
        dashboard: true,
        generalLock: false,
        withdraw: false,
        activeUser: false,
        marketAnalysis: false,
        casinoResult: false,
        deposit: false,
        agentAssign: false,
        userList: false,
        liveCasinoResult: false,
        creditReference: false,
        insertUser: false,
        ourCasino: false,
        userInfo: false,
        accountStatement: false,
        events: false,
        userPasswordChange: false,
        partyWinLoss: false,
        marketSearchAnalysis: false,
        userLock: false
      })
    } catch (error) {
      console.error('Error creating multi login account:', error)
      Toast.fire({
        icon: 'error',
        title: 'Failed to create account: ' + (error.message || 'Unknown error occurred')
      });
    } finally {
       fetchExecutives();
      setLoading(false)
    }
  }

  return (
    <Layout>
      <div className="p-4 w-full mx-auto bg-[#171b36] min-h-screen">
        {/* Personal Information Section */}
        <div
          // style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          // className="shadow-sm px-4 pt-4 mb-4"
        >
          <div
            style={{ color: "#cae0e8", fontSize: "24px" }}
            className="font-semibold mb-2"
          >
            Personal Information
          </div>
          <div className="w-full h-px bg-white mb-4"></div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-4 gap-1 mb-4">
              <div className="flex flex-col">
                <label className="text-white text-sm mb-1">
                  Client ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="clientId"
                  value={formData.clientId}
                  onChange={handleInputChange}
                  placeholder="Login Id"
                  className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="flex flex-col">
                <label className="text-white text-sm mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Full Name"
                  className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="flex flex-col">
                <label className="text-white text-sm mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Password"
                  className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="flex flex-col">
                <label className="text-white text-sm mb-1">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm Password"
                  className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div
                style={{ color: "#cae0e8", fontSize: "24px" }}
                className="font-semibold mb-2"
              >
                Privileges
              </div>

            {/* Privileges Section */}
            <div

                      style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 py-2 mb-4"
            
          >
            
         

              <div className="grid grid-cols-10  mb-4">
                {/* Row 1 */}
                <label className="flex items-center  cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.marketAnalysis}
                    onChange={() => handlePrivilegeChange('marketAnalysis')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Market Analysis</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.clientList}
                    onChange={() => handlePrivilegeChange('clientList')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Client List</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.withdraw}
                    onChange={() => handlePrivilegeChange('withdraw')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Withdraw</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.deposit}
                    onChange={() => handlePrivilegeChange('deposit')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Deposit</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.userStatus}
                    onChange={() => handlePrivilegeChange('userStatus')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">User Status</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.userUpdate}
                    onChange={() => handlePrivilegeChange('userUpdate')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">User Update</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.changePassword}
                    onChange={() => handlePrivilegeChange('changePassword')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Change Password</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.bankList}
                    onChange={() => handlePrivilegeChange('bankList')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Bank List</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.accountStatement}
                    onChange={() => handlePrivilegeChange('accountStatement')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Account Statement</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.myBets}
                    onChange={() => handlePrivilegeChange('myBets')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">My Bets</span>
                </label>

                {/* Row 2 */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.casinoReport}
                    onChange={() => handlePrivilegeChange('casinoReport')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Casino Report</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.userLogs}
                    onChange={() => handlePrivilegeChange('userLogs')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">User Logs</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.gameReport}
                    onChange={() => handlePrivilegeChange('gameReport')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Game Report</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.fraudReport}
                    onChange={() => handlePrivilegeChange('fraudReport')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Fraud Report</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.casinoList}
                    onChange={() => handlePrivilegeChange('casinoList')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Casino List</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.gameList}
                    onChange={() => handlePrivilegeChange('gameList')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Game List</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.creditReference}
                    onChange={() => handlePrivilegeChange('creditReference')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Credit Reference</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.exposureLimit}
                    onChange={() => handlePrivilegeChange('exposureLimit')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">Exposure Limit</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privileges.userCreate}
                    onChange={() => handlePrivilegeChange('userCreate')}
                    className="w-4 h-4"
                    style={{ accentColor: "#333d79ff" }}
                  />
                  <span className="text-white text-sm">User Create</span>
                </label>
              </div>

           
            </div>
               {/* Master Password and Submit */}
               <div className="flex items-end justify-end gap-2  mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-white text-sm mb-1">Master Password</label>
                  <input
                    type="password"
                    name="masterPassword"
                    value={formData.masterPassword}
                    onChange={handleInputChange}
                    placeholder="Master Password"
                    className="px-3 py-2 bg-white text-black  border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-[#333d79] text-white font-medium rounded-sm hover:bg-[#2d3569] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
              </div>
          </form>
        </div>

        {/* Table Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 py-2"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Action
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    User Name
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Full Name
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Game Report
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Fraud Report
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Casino List
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Game List
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    User Active
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Credit Reference
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Exposure Limit
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    User Create
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-2 py-8 text-center text-gray-400">
                      There are no records to show
                    </td>
                  </tr>
                ) : (
                  tableData.map((row, index) => (
                    <tr
                      key={row.id}
                      className={`transition-colors ${
                        index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""
                      }`}
                    >
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.action || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.username || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.fullName || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.gameReport ? 'Yes' : 'No'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.fraudReport ? 'Yes' : 'No'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.casinoList ? 'Yes' : 'No'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.gameList ? 'Yes' : 'No'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.userActive ? 'Yes' : 'No'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.creditReference ? 'Yes' : 'No'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.exposureLimit ? 'Yes' : 'No'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.userCreate ? 'Yes' : 'No'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default CreateAccount

